import { useState } from 'react';
import { formatDate, formatDateTime } from '../lib/format';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { AuditRow, CursorPage, InvoiceDetail, InvoicePaymentRow, PaymentSubmissionRow, TaxTreatment } from '@dru/types';
import { BRAND, EXAM_TYPE_LABEL, INVOICE_STATUS_LABEL, PAYMENT_METHOD_LABEL, TAX_TREATMENT_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Button, ConfirmDialog, DataTable, DetailHeader, DetailSection, EmptyState, ErrorState, Field, InfoTip, Input,
  KeyFigures, Select, StatSkeleton, StatusBadge, TextSkeleton, type Tone,
} from '@dru/ui';
import { api } from '../lib/api';
import { useQuery } from '../lib/useQuery';
import { useAuth } from '../lib/auth';
import { STANDING_LABEL, periodLabel, standingOf, type Standing } from '../consoles/hr/lib/billing-view';
import { PaymentSubmissionsSection } from './PaymentSubmissions';

/**
 * "Paid" is drawn in the settled colour only because `standingOf` reads the
 * confirmed ledger: a payment the company has submitted but Dr Ultimate has
 * not verified never makes an invoice PAID here.
 */
export const STANDING_TONE: Record<Standing, Tone> = {
  IN_PREPARATION: 'pending', UNPAID: 'dueSoon', PARTIAL: 'dueSoon', PAID: 'covered', VOID: 'neutral',
};

/** The document's state, for the platform. Issued is not "good" — it is sent. */
const DOC_TONE: Record<string, Tone> = { DRAFT: 'pending', ISSUED: 'info', VOID: 'neutral', DISCARDED: 'neutral' };

/** The audit trail in words, rather than the event keys the API records. */
const AUDIT_LABEL: Record<string, string> = {
  'invoice.generate': 'Invoice generated',
  'invoice.issue': 'Invoice issued',
  'invoice.discard': 'Draft discarded',
  'invoice.payment-recorded': 'Payment recorded',
  'invoice.payment-reversed': 'Payment reversed',
  'invoice.payment-submitted': 'Payment submitted by the company',
  'invoice.payment-submission-confirmed': 'Submitted payment confirmed',
  'invoice.payment-submission-rejected': 'Submitted payment rejected',
  'invoice.payment-submission-cancelled': 'Submitted payment withdrawn',
  'invoice.payment-proof-viewed': 'Payment proof viewed',
};
const auditLabel = (action: string) =>
  AUDIT_LABEL[action] ?? action.replace(/^[a-z]+\./, '').replace(/[._-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * A draft holds no number in the tax-invoice series: it is called
 * `DRAFT-7F3A9C21` until it is issued, which is when it takes the next
 * `INV/…` number. (A draft used to take that number when generated, so a
 * discarded one left a gap in the series.) Drafts generated before that
 * rule still show the number they hold.
 */
export const isDraftNumber = (invoiceNumber: string) => invoiceNumber.startsWith('DRAFT-');

/** What to call an invoice on a screen: its number once it has one, "Draft invoice" until then. */
export function invoiceName(i: { invoiceNumber: string }): string {
  return isDraftNumber(i.invoiceNumber) ? 'Draft invoice' : i.invoiceNumber;
}

const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;

/**
 * GST charged (standard) or shifted to the buyer (reverse charge) on an
 * invoice with no buyer GSTIN.
 *
 * A warning and never a block — an unregistered buyer may be invoiced — but
 * it has to be said before the invoice is issued, because an issued invoice
 * cannot be edited: an 18% invoice went out to a company with a blank GSTIN
 * and nothing on the screen mentioned it (audit of 1 Oct 2026). The same rule
 * as the API's `buyerGstinMissing`, which marks the issue in the audit trail.
 */
export function GstinMissingWarning({ taxTreatment, billToGstin, billToName, stage, className }: {
  taxTreatment: string; billToGstin: string | null | undefined; billToName: string;
  /** Before the act, or on the invoice afterwards. */
  stage: 'issuing' | 'issued';
  className?: string;
}) {
  const reverse = taxTreatment === 'REVERSE_CHARGE';
  if ((taxTreatment !== 'GST_STANDARD' && !reverse) || filled(billToGstin)) return null;
  return (
    <Alert tone="warning" className={className} title={stage === 'issuing' ? `${billToName} has no GSTIN on record` : 'Issued with no buyer GSTIN'}>
      {stage === 'issuing' ? (
        reverse
          ? 'Reverse charge moves the tax to the buyer, which presumes a GST-registered buyer — and neither the plant nor the company has a GSTIN. Check the treatment, or add the GSTIN to the company or plant first. You can still issue; an issued invoice cannot be edited.'
          : 'Neither the plant nor the company has a GSTIN, so this GST invoice will carry no buyer GSTIN and the company cannot claim input tax credit on it. That is valid for an unregistered buyer. If the company is registered, add its GSTIN to the company or plant first — an issued invoice cannot be edited.'
      ) : (
        reverse
          ? 'This invoice is under reverse charge and carries no buyer GSTIN. It stands as issued; if that is wrong, it is corrected with a credit note and a new invoice.'
          : 'GST is charged on this invoice and it carries no buyer GSTIN, as for an unregistered buyer. It stands as issued; if the company is registered, it is corrected with a credit note and a new invoice.'
      )}
    </Alert>
  );
}

/**
 * One invoice, in full, for both consoles.
 *
 * The platform reaches it with `?tenantId=` and may issue it; the company
 * reads its own. Same lines, same totals, same payment ledger — the only
 * difference is the action row.
 *
 * Four amounts are kept apart on purpose, because a plant's accounts team
 * reconciles against each one: what was invoiced, what Dr Ultimate has
 * confirmed as received, what has been submitted and is still being
 * verified, and what is outstanding. Outstanding is the API's figure and
 * counts confirmed payments only — a submission never lowers it.
 */
export function InvoiceDetailPage({ console: which }: { console: 'admin' | 'hr' }) {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const tenantId = params.get('tenantId') ?? '';
  const { permissions } = useAuth();
  const q = which === 'admin' && tenantId ? `?tenantId=${tenantId}` : '';

  // A company user without invoice.read has nothing to fetch here; the
  // screen says so rather than rendering the API's refusal as an error. The
  // platform holds no company of its own, so without `?tenantId=` it cannot
  // ask for the invoice at all — the API would refuse with a 400.
  const allowed = which === 'admin' ? !!tenantId : permissions.has('invoice.read');
  const invoice = useQuery<InvoiceDetail | null>(() => (allowed ? api.get(`/invoices/${id}${q}`) : Promise.resolve(null)), [id, q, allowed]);
  const payments = useQuery<{ items: InvoicePaymentRow[] } | InvoicePaymentRow[]>(
    () => !allowed ? Promise.resolve({ items: [] as InvoicePaymentRow[] }) : api.get<{ items: InvoicePaymentRow[] } | InvoicePaymentRow[]>(`/invoices/${id}/payments${q}`).catch(() => ({ items: [] as InvoicePaymentRow[] })), [id, q, allowed]);
  // What the company has said it paid, awaiting or past Dr Ultimate's
  // verification. Separate from the ledger above: a submission is not money.
  const submissions = useQuery<{ items: PaymentSubmissionRow[] }>(
    () => !allowed ? Promise.resolve({ items: [] }) : api.get<{ items: PaymentSubmissionRow[] }>(`/invoices/${id}/payment-submissions${q}`).catch(() => ({ items: [] })), [id, q, allowed]);
  // The trail is for those who read the audit log; a plant's accounts login
  // does not, and should not be shown an empty table implying there is none.
  const canAudit = allowed && permissions.has('audit.read');
  const trail = useQuery<CursorPage<AuditRow>>(() => !canAudit ? Promise.resolve({ items: [] as AuditRow[], nextCursor: null }) : api.get<CursorPage<AuditRow>>(`/audit?entityType=Invoice&entityId=${id}&limit=50${tenantId && which === 'admin' ? `&tenantId=${tenantId}&reason=invoice%20review` : ''}`).catch(() => ({ items: [] as AuditRow[], nextCursor: null })), [id, tenantId, which, canAudit]);
  const [treatment, setTreatment] = useState<TaxTreatment>('UNDETERMINED');
  const [ratePercent, setRatePercent] = useState('18');
  const [confirmIssue, setConfirmIssue] = useState(false);

  const inv = invoice.data;
  const paid = Array.isArray(payments.data) ? payments.data : (payments.data?.items ?? []);
  const canIssue = which === 'admin' && permissions.has('invoice.approve');
  const money = (p: number) => formatPaise(p, inv?.currency);
  const rateBp = Math.round(Number(ratePercent) * 100);
  const rateError = treatment === 'GST_STANDARD' && !(rateBp > 0 && rateBp <= 2800) ? 'Enter a GST rate between 0.01 and 28.' : null;

  async function issue() {
    await api.post(`/invoices/${id}/issue${q}`, {
      taxTreatment: treatment,
      ...(treatment === 'GST_STANDARD' ? { taxRateBasisPoints: rateBp } : {}),
    });
    setConfirmIssue(false);
    invoice.reload();
  }

  const standing = inv ? standingOf(inv) : null;
  const pending = inv?.pendingSubmittedPaise ?? 0;
  const issued = inv?.status === 'ISSUED';

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Invoice"
        title={inv ? invoiceName(inv) : (invoice.loading && allowed ? 'Loading invoice…' : 'Invoice')}
        badges={inv && standing && (
          <>
            {which === 'admin' && <StatusBadge tone={DOC_TONE[inv.status] ?? 'neutral'}>{INVOICE_STATUS_LABEL[inv.status] ?? inv.status}</StatusBadge>}
            {/* The company reads where the bill stands — paid, partly paid,
                unpaid. The platform reads it too, once the invoice is issued. */}
            {(which === 'hr' || issued) && <StatusBadge tone={STANDING_TONE[standing]}>{STANDING_LABEL[standing]}</StatusBadge>}
            {issued && pending > 0 && <StatusBadge tone="pending">Payment awaiting verification</StatusBadge>}
          </>
        )}
        subtitle={inv && [
          `Plant ${inv.billToPlantCode} · ${inv.billToPlantName}`,
          periodLabel(inv.periodStart, inv.periodEnd),
          inv.issuedAt ? `Issued ${formatDate(inv.issuedAt)}` : 'Not issued yet',
          // A draft's working name, for anyone who has to refer to it before it has a number.
          ...(isDraftNumber(inv.invoiceNumber) ? [`Ref ${inv.invoiceNumber} — numbered when issued`] : []),
        ].join(' · ')}
      />

      {which === 'hr' && !allowed && (
        <EmptyState title="Billing is not part of your access" hint="Invoices are read by your company administrator and plant accounts team." />
      )}
      {which === 'admin' && !allowed && (
        <EmptyState
          title="Open this invoice from its company"
          hint="An invoice belongs to one company, and this link does not say which. Open it from Billing or Payment review, where the company is known."
          action={<Link to="/admin/billing" className="text-small font-medium text-primary-600 hover:text-primary-700">Go to Billing</Link>}
        />
      )}
      {invoice.error && (
        <ErrorState
          title="This invoice cannot be shown"
          detail={`${invoice.error.replace(/([^.!?])$/, '$1.')}${which === 'hr' ? ' It may belong to a plant your account does not cover, or the link may be out of date.' : ''}`}
          onRetry={invoice.reload}
        />
      )}
      {allowed && invoice.loading && !inv && (
        <div className="space-y-4"><StatSkeleton count={4} /><TextSkeleton lines={6} /></div>
      )}

      {inv && (
        <>
          <KeyFigures
            label="Invoice amounts"
            figures={[
              { key: 'total', label: 'Invoice amount', value: money(inv.totalPaise),
                hint: inv.taxTreatment === 'UNDETERMINED' ? 'Tax not decided yet' : `Incl. ${TAX_TREATMENT_LABEL[inv.taxTreatment]}` },
              { key: 'paid', label: 'Paid (confirmed)', value: issued ? money(inv.paidPaise ?? 0) : '—',
                hint: `Verified by ${BRAND.name}` },
              { key: 'pending', label: 'Awaiting verification', value: issued ? money(pending) : '—',
                tone: pending > 0 ? 'pending' : undefined,
                hint: pending > 0 ? 'Not yet counted as paid' : 'None submitted' },
              { key: 'out', label: 'Outstanding', value: issued ? money(inv.outstandingPaise ?? 0) : '—',
                tone: issued && (inv.outstandingPaise ?? 0) > 0 ? 'overdue' : undefined,
                hint: !issued
                  ? (inv.status === 'DRAFT' ? 'Owed once issued' : 'Void — nothing owed')
                  : pending > 0 ? 'Drops once confirmed' : 'After confirmed payments' },
            ]}
          />

          <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
            <div className="min-w-0 space-y-4">
              <DetailSection title={`Line items (${inv.lines.length})`}>
                <DataTable<InvoiceDetail['lines'][number]>
                  rows={inv.lines}
                  rowKey={(l) => l.id}
                  dense
                  empty={{ title: 'No line items on this invoice', hint: 'A draft gains lines when certified examinations in its period are billed.' }}
                  columns={[
                    { key: 'who', header: 'Employee', alwaysVisible: true, cell: (l) => (
                      <div>
                        <div className="text-neutral-900">{l.order?.employee.fullName ?? l.description}</div>
                        {l.order && <div className="font-mono text-caption text-neutral-500">{l.order.employee.employeeCode}</div>}
                      </div>
                    ) },
                    { key: 'item', header: which === 'hr' ? 'Item' : 'Priced by', cell: (l) => <span className="text-small text-neutral-600">{which === 'hr' ? lineItem(l) : rateBasisLabel(l.rateBasis)}</span> },
                    { key: 'date', header: 'Service date', cell: (l) => <span className="whitespace-nowrap text-small text-neutral-600">{formatDate(l.servicedOn)}</span> },
                    { key: 'amount', header: 'Amount', align: 'right', cell: (l) => <span className="whitespace-nowrap tabular-nums">{money(l.amountPaise)}</span> },
                  ]}
                />
              </DetailSection>

              <DetailSection title="Invoice summary">
                <dl className="space-y-1 text-small">
                  <SumRow label="Subtotal" value={money(inv.subtotalPaise)} />
                  <SumRow
                    label={inv.taxTreatment === 'UNDETERMINED' ? 'Tax' : `Tax · ${TAX_TREATMENT_LABEL[inv.taxTreatment]}${inv.taxRateBasisPoints ? ` ${inv.taxRateBasisPoints / 100}%` : ''}`}
                    value={inv.taxTreatment === 'UNDETERMINED' ? 'Decided at issue' : money(inv.taxPaise)}
                  />
                  <SumRow label="Invoice amount" value={money(inv.totalPaise)} strong rule />
                  {issued && <SumRow label="Less confirmed payments" value={`− ${money(inv.paidPaise ?? 0)}`} />}
                  {issued && <SumRow label="Outstanding" value={money(inv.outstandingPaise ?? 0)} strong rule />}
                  {issued && pending > 0 && (
                    <SumRow label="Submitted, awaiting verification (not deducted)" value={money(pending)} muted />
                  )}
                </dl>
                {inv.notes && <p className="mt-2 text-caption text-neutral-600">{inv.notes}</p>}
                {inv.voidReason && <p className="mt-2 text-caption text-neutral-600">Void reason: {inv.voidReason}</p>}
              </DetailSection>
            </div>

            {/* First on a phone: the act (issue, submit a payment) and the
                money trail, before a long list of lines. */}
            <div className="order-first min-w-0 space-y-4 lg:order-none">
              {canIssue && inv.status === 'DRAFT' && (
                <DetailSection title="Issue invoice" action={(
                  <InfoTip label="About issuing">
                    Choose the GST treatment, then issue. Issuing locks the invoice and sends it to the company as a bill.
                  </InfoTip>
                )}>
                  <div className="grid gap-3 sm:grid-cols-[1fr_7rem]">
                    <Field label="Tax treatment" required>
                      {(p) => (
                        <Select {...p} value={treatment} onChange={(e) => setTreatment(e.target.value as TaxTreatment)}>
                          <option value="UNDETERMINED">Select tax treatment…</option>
                          <option value="GST_STANDARD">Standard GST</option>
                          <option value="EXEMPT">Healthcare exemption</option>
                          <option value="REVERSE_CHARGE">Reverse charge</option>
                        </Select>
                      )}
                    </Field>
                    {treatment === 'GST_STANDARD' && (
                      <Field label="Rate (%)" error={rateError} required>
                        {(p) => <Input {...p} value={ratePercent} inputMode="decimal" onChange={(e) => setRatePercent(e.target.value)} />}
                      </Field>
                    )}
                  </div>
                  <GstinMissingWarning
                    className="mt-3" stage="issuing" taxTreatment={treatment}
                    billToGstin={inv.billToGstin} billToName={inv.billToName}
                  />
                  <div className="mt-3 flex justify-end">
                    <Button onClick={() => setConfirmIssue(true)} disabled={treatment === 'UNDETERMINED' || !!rateError}>Issue invoice…</Button>
                  </div>
                </DetailSection>
              )}

              {(which === 'hr' || (submissions.data?.items.length ?? 0) > 0) && (
                <PaymentSubmissionsSection
                  invoice={inv}
                  items={submissions.data?.items ?? []}
                  onChanged={() => { submissions.reload(); invoice.reload(); }}
                  {...(which === 'admin' ? { platformTenantId: tenantId } : {})}
                />
              )}

              <DetailSection title={`Confirmed payments (${paid.length})`} action={(which === 'hr' || tenantId) ? (
                <InfoTip label="About confirmed payments">
                  {which === 'hr'
                    ? `Payments ${BRAND.name} has verified and recorded. Only these count as paid.`
                    : 'Payments are recorded from the company’s Billing tab, or by confirming a submission in Payment review.'}
                </InfoTip>
              ) : undefined}>
                {paid.length === 0 ? <p className="text-small text-neutral-500">No payment has been confirmed against this invoice yet.</p> : (
                  <ul className="divide-y divide-neutral-100 text-small">
                    {paid.map((p) => (
                      <li key={p.id} className="flex items-start justify-between gap-3 py-2">
                        <span className="min-w-0 text-neutral-800">
                          {formatDate(p.receivedOn)} · {PAYMENT_METHOD_LABEL[p.method] ?? p.method}
                          {p.reference && <> · <span className="font-mono">{p.reference}</span></>}
                          {p.reversesId && <span className="text-neutral-500"> · reversal{p.reversalReason ? `: ${p.reversalReason}` : ''}</span>}
                        </span>
                        <span className="shrink-0 tabular-nums text-neutral-900">{money(p.amountPaise)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </DetailSection>

              <DetailSection title="Billed to" phoneFolded action={inv.status === 'DRAFT' ? (
                <InfoTip label="About the billing details">
                  Draft: shown as the company and plant records stand now; the billing details are fixed when the invoice is issued.
                </InfoTip>
              ) : undefined}>
                {/* The frozen bill-to: an issued invoice reads exactly as it was
                    sent, whatever the plant or company records say today. */}
                <div className="space-y-0.5 text-small text-neutral-700">
                  <div className="font-medium text-neutral-900">{inv.billToName}</div>
                  <div className="text-neutral-600">Plant {inv.billToPlantCode} · {inv.billToPlantName}</div>
                  {inv.billToGstin
                    ? <div>GSTIN <span className="font-mono">{inv.billToGstin}</span>{inv.billToSource === 'PLANT' ? ' (plant)' : ' (company)'}</div>
                    : <div className="text-neutral-600">{issued ? 'No buyer GSTIN on this invoice' : 'No GSTIN on record'}</div>}
                  {/* The plant's own address when it has one. The company's
                      stands in only when the plant has none, and then the
                      text itself begins "Registered office:" — frozen that
                      way at issue, so it is never read as the plant's. */}
                  {inv.billToAddress && <div className="text-neutral-600">{inv.billToAddress}</div>}
                  {inv.billToEmail && <div className="text-neutral-600">{inv.billToEmail}</div>}
                  {inv.rateCard && <div className="pt-1 text-caption text-neutral-500">Priced on rate card “{inv.rateCard.name}”</div>}
                </div>
                {issued && (
                  <GstinMissingWarning
                    className="mt-2" stage="issued" taxTreatment={inv.taxTreatment}
                    billToGstin={inv.billToGstin} billToName={inv.billToName}
                  />
                )}
              </DetailSection>
            </div>

            {canAudit && (
              <DetailSection title="Activity" className="xl:col-span-2">
                <DataTable<AuditRow>
                  rows={trail.data?.items ?? []} loading={trail.loading} error={trail.error} onRetry={trail.reload} rowKey={(r) => r.id}
                  dense
                  empty={{ title: 'No activity recorded yet', hint: 'Generating, issuing and payments on this invoice are recorded here.' }}
                  columns={[
                    { key: 'what', header: 'Action', alwaysVisible: true, cell: (r) => <span className="text-neutral-900">{auditLabel(r.action)}</span> },
                    { key: 'when', header: 'When', cell: (r) => <span className="whitespace-nowrap text-small text-neutral-600">{formatDateTime(r.occurredAt)}</span> },
                    { key: 'outcome', header: 'Result', cell: (r) => (
                      <StatusBadge tone={r.outcome === 'SUCCESS' ? 'neutral' : 'danger'}>{r.outcome === 'SUCCESS' ? 'Done' : 'Refused'}</StatusBadge>
                    ) },
                  ]}
                />
              </DetailSection>
            )}
          </div>

          {confirmIssue && (
            <ConfirmDialog
              title={isDraftNumber(inv.invoiceNumber) ? 'Issue this invoice?' : `Issue ${inv.invoiceNumber}?`}
              what={`The invoice is locked and sent to ${inv.billToName} as a bill for ${plantLine(inv)}.`}
              affects={(
                <dl className="space-y-0.5">
                  {isDraftNumber(inv.invoiceNumber) && <SumRow label="Invoice number" value="Next in the series, on issue" />}
                  <SumRow label="Subtotal" value={money(inv.subtotalPaise)} />
                  <SumRow label={`Tax · ${TAX_TREATMENT_LABEL[treatment]}${treatment === 'GST_STANDARD' ? ` ${ratePercent}%` : ''}`} value="Calculated on issue" />
                  <SumRow label="Buyer GSTIN" value={filled(inv.billToGstin) ? inv.billToGstin! : 'None on record'} />
                  <SumRow label="Lines" value={String(inv.lines.length)} />
                </dl>
              )}
              irreversible="An issued invoice cannot be edited or un-issued. A mistake is corrected with a credit or debit note."
              confirmLabel="Issue invoice"
              onConfirm={issue}
              onCancel={() => setConfirmIssue(false)}
            >
              {/* Said again at the last step: this is the click that cannot be taken back. */}
              <GstinMissingWarning
                stage="issuing" taxTreatment={treatment}
                billToGstin={inv.billToGstin} billToName={inv.billToName}
              />
            </ConfirmDialog>
          )}
        </>
      )}
    </div>
  );
}

function plantLine(inv: InvoiceDetail) {
  return `plant ${inv.billToPlantCode}, ${periodLabel(inv.periodStart, inv.periodEnd)}`;
}

function SumRow({ label, value, strong, rule, muted }: { label: string; value: string; strong?: boolean; rule?: boolean; muted?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${rule ? 'border-t border-neutral-200 pt-1' : ''} ${strong ? 'font-medium text-neutral-900' : muted ? 'text-neutral-600' : 'text-neutral-700'}`}>
      <dt>{label}</dt><dd className="shrink-0 text-right tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * The rate card match a line records — "examination type PRE_EMPLOYMENT" —
 * with the enum read as the product says it: "Examination type: Pre-employment".
 */
function rateBasisLabel(basis: string): string {
  const words = basis.replace(/\b[A-Z][A-Z0-9_]{2,}\b/g, (m) => EXAM_TYPE_LABEL[m] ?? (m.includes('_') ? m.replace(/_/g, ' ').toLowerCase() : m));
  const m = words.match(/^(examination type|protocol|package|test|extra test)\s+(.*)$/i);
  const out = m ? `${m[1]}: ${m[2]}` : words;
  return out.charAt(0).toUpperCase() + out.slice(1);
}

/**
 * What a line charges for, in words — the company reads "Periodic (OHC)",
 * not the rate card's matching key.
 */
function lineItem(l: InvoiceDetail['lines'][number]): string {
  if (l.kind === 'EXTRA_TEST') return `Additional test${l.test ? `: ${l.test.name}` : ''}`;
  const type = l.order ? (EXAM_TYPE_LABEL[l.order.examinationType] ?? l.order.examinationType) : 'Examination';
  return `${type} examination`;
}

